import { Dialog } from '@/components/UI'
import { HtmlContent } from '@/components/UI/HtmlContent'
import type React from 'react'
import { memo } from 'react'

type Props = {
  opened: boolean
  children: React.ReactNode
  title?: string
  description?: string
  image?: string | null
  point?: number | null
  /**
   * 獲得したスタンプ名（#2540）。指定時のみ画像の下に表示する。
   * 1スポットに複数スタンプが紐づく場合、どのスタンプを獲得したのか判別できるようにするため。
   * 表示位置・スタイルは DialogRedisplayStamps に合わせている。
   */
  stampName?: string
  error?: string
  onClose?: () => void
  closeButtonOff?: boolean
  campaign: {
    type: number
    stampShape?: number | null
    stampPopup?: { icons: string[]; message: string } | null
  }
}

export const DialogGetStamps = memo(
  ({
    opened,
    children,
    title,
    description,
    image,
    point,
    stampName,
    error,
    onClose,
    campaign,
    closeButtonOff = false,
  }: Props) => {
    return (
      <Dialog selectorState={opened} closeFunc={onClose} showCloseButton={!closeButtonOff} title={title}>
        {error && <p className="mt-5 mb-5 text-center text-sm">{error}</p>}

        {campaign.type === 1 && image && (
          <>
            {/* 丸スタンプ: 非正方形画像でも中央基準でトリミングして円形に収める（#3450） */}
            {campaign.stampShape !== 1 && (
              <div className="mx-auto mt-5 h-40 w-40 overflow-hidden rounded-full">
                <img src={image} alt="" data-testid="stamp-dialog-image" className="h-full w-full object-cover" />
              </div>
            )}
            {campaign.stampShape === 1 && (
              <div className="relative mx-auto mt-5 h-40 w-40 overflow-hidden text-center">
                <img
                  src={image}
                  alt=""
                  data-testid="stamp-dialog-image"
                  className="absolute top-1/2 left-1/2 h-auto w-auto max-h-full max-w-full -translate-x-1/2 -translate-y-1/2 object-contain"
                />
              </div>
            )}
          </>
        )}

        {stampName && <p className="text-gray-600 mt-5 text-center text-sm font-bold">{stampName}</p>}

        {/* 0pt 付与のスタンプでも数値を出すため truthy ではなく null/undefined のみ除外する */}
        {(campaign.type === 2 || campaign.type === 3) && point != null && (
          <div className="mx-auto mt-5 text-center">
            <p className="text-[140px] font-bold">
              {point}
              <span className="text-[30px]">pt</span>
            </p>
          </div>
        )}

        {description && <HtmlContent html={description} className="text-gray-600 mt-5 text-center text-sm font-bold" />}

        {campaign.stampPopup && (
          <>
            <div className="mt-8 flex justify-center">
              {campaign.stampPopup.icons.map((icon, i) => {
                return <img key={i} src={icon} className="mx-2 h-8 w-8 object-contain" alt="" />
              })}
            </div>

            <HtmlContent html={campaign.stampPopup.message} className="text-gray-600 mt-5 text-sm font-bold" />
          </>
        )}

        {children}
      </Dialog>
    )
  },
)
